import { useLocalStorage } from "@mantine/hooks";
import { UserAppearance } from "@src/gql/graphql";
import { useCallback, useEffect, useState } from "react";

export const COLOR_SCHEME_STORAGE_KEY = "illumi-color-scheme";

export type AppColorScheme = "light" | "dark";
export type AppThemePreference = "light" | "dark" | "auto";

export function isAppColorScheme(value: unknown): value is AppColorScheme {
	return value === "light" || value === "dark";
}

export function isAppThemePreference(
	value: unknown,
): value is AppThemePreference {
	return value === "light" || value === "dark" || value === "auto";
}

export function toThemePreference(
	appearance: UserAppearance,
): AppThemePreference {
	switch (appearance) {
		case UserAppearance.Dark:
			return "dark";
		case UserAppearance.Light:
			return "light";
		default:
			return "auto";
	}
}

export function toUserAppearance(
	preference: AppThemePreference,
): UserAppearance {
	switch (preference) {
		case "dark":
			return UserAppearance.Dark;
		case "light":
			return UserAppearance.Light;
		default:
			return UserAppearance.Auto;
	}
}

export function getSystemColorScheme(): AppColorScheme {
	if (
		typeof window === "undefined" ||
		typeof window.matchMedia !== "function"
	) {
		return "light";
	}
	try {
		return window.matchMedia("(prefers-color-scheme: dark)").matches
			? "dark"
			: "light";
	} catch {
		return "light";
	}
}

/**
 * Resolve storage to an effective light/dark scheme. Explicit light/dark
 * win; `auto`, missing, and invalid values follow the OS.
 */
export function resolveColorScheme(raw: unknown): AppColorScheme {
	if (isAppColorScheme(raw)) {
		return raw;
	}
	return getSystemColorScheme();
}

/**
 * Read localStorage preference and return the effective scheme. Does not
 * seed a fixed light/dark value — missing, invalid, and `auto` follow OS.
 */
export function getDefaultColorScheme(): AppColorScheme {
	if (typeof window === "undefined") {
		return "light";
	}
	try {
		const raw = window.localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
		return resolveColorScheme(raw);
	} catch {
		return getSystemColorScheme();
	}
}

export function useAppColorScheme() {
	const [defaultPreference] = useState<AppThemePreference>(() => {
		if (typeof window === "undefined") return "auto";
		try {
			const raw = window.localStorage.getItem(COLOR_SCHEME_STORAGE_KEY);
			return isAppThemePreference(raw) ? raw : "auto";
		} catch {
			return "auto";
		}
	});

	const [preference, setPreferenceRaw] = useLocalStorage<AppThemePreference>({
		key: COLOR_SCHEME_STORAGE_KEY,
		defaultValue: defaultPreference,
		getInitialValueInEffect: false,
		serialize: (value) => value,
		deserialize: (value) => {
			if (isAppThemePreference(value)) {
				return value;
			}
			return "auto";
		},
	});

	const [systemScheme, setSystemScheme] =
		useState<AppColorScheme>(getSystemColorScheme);

	useEffect(() => {
		if (
			typeof window === "undefined" ||
			typeof window.matchMedia !== "function"
		) {
			return;
		}

		const media = window.matchMedia("(prefers-color-scheme: dark)");
		const handleChange = (event: MediaQueryListEvent | MediaQueryList) => {
			setSystemScheme(event.matches ? "dark" : "light");
		};

		setSystemScheme(media.matches ? "dark" : "light");

		if (typeof media.addEventListener === "function") {
			media.addEventListener("change", handleChange);
			return () => media.removeEventListener("change", handleChange);
		}
		if (typeof media.addListener === "function") {
			media.addListener(handleChange);
			return () => media.removeListener(handleChange);
		}
	}, []);

	const effectiveColorScheme: AppColorScheme =
		preference === "auto" ? systemScheme : preference;

	const setPreference = useCallback(
		(next: AppThemePreference) => {
			setPreferenceRaw(next);
		},
		[setPreferenceRaw],
	);

	const toggleColorScheme = useCallback(() => {
		setPreferenceRaw((prev) => {
			const current = prev === "auto" ? systemScheme : prev;
			return current === "dark" ? "light" : "dark";
		});
	}, [setPreferenceRaw, systemScheme]);

	return {
		colorScheme: effectiveColorScheme,
		preference,
		setPreference,
		toggleColorScheme,
	};
}
